import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, Info, Loader2, Send } from 'lucide-react';
import {
  trackerFdfcDispatchProblems,
  type TrackerFdfcDispatchResult,
  type TrackerFdfcEntry,
} from '@tracker/shared';
import { trackerFdfcService } from '@/services/tracker-fdfc.service';
import { getErrorMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { KitPicker } from './KitPicker';

export function TrackerFdfcDispatchDialog({
  open,
  entries,
  onClose,
  onSent,
}: {
  open: boolean;
  /** The ticked entries, as shown in the table. The server re-checks each one. */
  entries: TrackerFdfcEntry[];
  onClose: () => void;
  /** Called after a run so the page can clear its selection. */
  onSent: () => void;
}) {
  const queryClient = useQueryClient();
  const [kitCode, setKitCode] = useState('');
  const [result, setResult] = useState<TrackerFdfcDispatchResult | null>(null);

  const checked = entries.map((e) => ({ entry: e, problems: trackerFdfcDispatchProblems(e) }));
  const ready = checked.filter((c) => c.problems.length === 0);
  const notReady = checked.filter((c) => c.problems.length > 0);

  const send = useMutation({
    mutationFn: () => trackerFdfcService.dispatch(ready.map((c) => c.entry.id), kitCode),
    onSuccess: (r) => {
      setResult(r);
      onSent();
      void queryClient.invalidateQueries({ queryKey: ['tracker-fdfc'] });
      const msg = `${r.sent.length} sent to NSDC`;
      if (r.failed.length || r.skipped.length) toast.warning(`${msg} · ${r.failed.length + r.skipped.length} not sent`);
      else toast.success(msg);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const close = () => {
    if (send.isPending) return;
    setResult(null);
    setKitCode('');
    send.reset();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{result ? 'Dispatch result' : `Dispatch ${entries.length} to NSDC`}</DialogTitle>
          <DialogDescription>
            Each person becomes one kit order at NSDC (portal). Orders wait on NSDC&apos;s
            Orientation Approvals tab until NSDC staff release them for shipping.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <DispatchSummary result={result} onDone={close} />
        ) : (
          <div className="space-y-4">
            {/* Kit */}
            <KitPicker enabled={open} kitCode={kitCode} onChange={setKitCode} />

            {/* Recipients */}
            <div>
              <h3 className="mb-1.5 text-sm font-semibold">
                Recipients · {ready.length} ready
                {notReady.length > 0 && <span className="text-rose-600 dark:text-rose-400"> · {notReady.length} can&apos;t be sent</span>}
              </h3>
              <div className="max-h-64 overflow-auto rounded-lg border border-border/60">
                <table className="w-full text-sm">
                  <tbody>
                    {checked.map(({ entry, problems }) => (
                      <tr key={entry.id} className="border-b border-border/40 last:border-0 align-top">
                        <td className="px-3 py-2">
                          <div className="font-medium">{entry.full_name ?? '—'}</div>
                          <div className="font-mono text-xs text-muted-foreground">{entry.mobile_number}</div>
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          {[entry.city, entry.state, entry.pincode].filter(Boolean).join(', ') || '—'}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {problems.length === 0 ? (
                            <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                              <CheckCircle2 className="h-3.5 w-3.5" /> Ready
                            </span>
                          ) : (
                            <span className="text-rose-600 dark:text-rose-400">{problems.join('; ')}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Sent one by one (about 1 second each) so NSDC is not overloaded. Keep this window open
              until it finishes. NSDC does not block duplicates, so each person can be sent only once
              from here.
            </p>

            <DialogFooter>
              <Button variant="outline" onClick={close} disabled={send.isPending}>Cancel</Button>
              <Button onClick={() => send.mutate()} disabled={!kitCode || ready.length === 0 || send.isPending}>
                {send.isPending ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Sending {ready.length}…</>
                ) : (
                  <><Send className="h-4 w-4" /> Send {ready.length} to NSDC</>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DispatchSummary({ result, onDone }: { result: TrackerFdfcDispatchResult; onDone: () => void }) {
  const notSent = [
    ...result.failed.map((f) => ({ id: f.id, name: f.name, why: f.error })),
    ...result.skipped.map((s) => ({ id: s.id, name: s.name, why: s.reason })),
  ];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-lg border border-border/60 p-3 text-center">
          <p className="text-2xl font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">{result.sent.length}</p>
          <p className="text-xs text-muted-foreground">Sent to NSDC</p>
        </div>
        <div className="rounded-lg border border-border/60 p-3 text-center">
          <p className={cn('text-2xl font-semibold tabular-nums', notSent.length ? 'text-rose-600 dark:text-rose-400' : 'text-muted-foreground')}>
            {notSent.length}
          </p>
          <p className="text-xs text-muted-foreground">Not sent</p>
        </div>
      </div>

      {result.sent.length > 0 && (
        <div className="max-h-40 overflow-auto rounded-lg border border-border/60 text-sm">
          {result.sent.map((s) => (
            <div key={s.id} className="flex justify-between border-b border-border/40 px-3 py-1.5 last:border-0">
              <span>{s.name}</span>
              <span className="font-mono text-xs">{s.order_code}</span>
            </div>
          ))}
        </div>
      )}

      {notSent.length > 0 && (
        <div>
          <h3 className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold">
            <AlertTriangle className="h-4 w-4 text-amber-500" /> Not sent
          </h3>
          <div className="max-h-48 overflow-auto rounded-lg border border-border/60 text-sm">
            {notSent.map((n) => (
              <div key={n.id} className="border-b border-border/40 px-3 py-1.5 last:border-0">
                <div className="font-medium">{n.name}</div>
                <div className="text-xs text-muted-foreground">{n.why}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <DialogFooter>
        <Button onClick={onDone}>Done</Button>
      </DialogFooter>
    </div>
  );
}
